<template>
    <div class="annotation-page">
        <topPanel
                :projectName="projectName"
                :status="status"
                :drawingVersion="drawingVersion"
                :currentVersion="currentVersion"
        />
        <div class="annotation-layout">
            <leftPanel
                    :drawingGroups="drawingGroups"
                    :activeDrawing="activeDrawing"
                    :activePrevDrawing="activePrevDrawing"
                    :currentVersion="currentVersion"
                    :historyVersions="historyVersions"
                    :activeVersion="activeVersion"
                    :compareMode="showPrevious"
                    :viewMode="viewMode"
                    :auditId="auditId"
                    :versionNos="versionNos"
                    :activeGroup="activeGroup"
                    @select="onSelectDrawing"
                    @select-prev="onSelectPrevDrawing"
                    @select-version="onSelectVersion"
                    @compare="togglePreviousFiles"
                    @select-group="onSelectGroup"
            />
            <centerPanel
                    v-if="!showPrevious"
                    ref="centerPanel"
                    :baseImageUrl="baseImageUrl"
                    :currentPage.sync="currentPage"
                    :totalPages.sync="totalPages"
                    :zoom.sync="zoom"
                    :loginName="loginName"
                    :userName="userName"
                    :userId="userId"
                    :pageId="currentPageId"
                    :activeAnnoId="activeAnnotation"
                    :viewMode="viewMode"
                    @select-anno="onSelectAnnotation"
                    @add-annotation="onAddAnnotationFromCanvas"
                    @cache-sync="onCanvasCacheSync"
                    @reply="handleReply"
            />
            <contrastCenterPanel
                    v-else
                    ref="contrastPanel"
                    :topBaseImageUrl="baseImageUrl"
                    :bottomBaseImageUrl="bottomBaseImageUrl"
                    :topCurrentPage="currentPage"
                    :bottomCurrentPage="bottomCurrentPage"
                    :topTotalPages="totalPages"
                    :bottomTotalPages="bottomTotalPages"
                    :topZoom="zoom"
                    :bottomZoom="zoom"
                    :topPageId="currentPageId"
                    :bottomPageId="bottomPageId"
                    :topVersion="topVersionLabel"
                    :bottomVersion="selectedVersionLabel || prevVersionLabel"
                    :loginName="loginName"
                    :userName="userName"
                    :userId="userId"
                    :viewMode="viewMode"
                    @select-anno="onSelectAnnotation"
                    @add-annotation="onAddAnnotationFromCanvas"
                    @cache-sync="onCanvasCacheSync"
                    @reply="handleReply"
            />
            <rightPanel
                    :annotationList="annotationList"
                    :activeAnnotation="activeAnnotation"
                    :viewMode="viewMode"
                    :pendingReplies="pendingReplies"
                    :activeAnnoReplyContent="activeAnnoReplyContent"
                    :userId="userId"
                    :isPrevDrawing="currentIsPrevDrawing"
                    :compareMode="showPrevious"
                    @select="onSelectAnnotation"
                    @add="onAddAnnotation"
                    @delete="onDeleteAnnotation"
                    @update-annotation="onUpdateAnnotation"
                    @refresh="noop"
                    @save="handleSave"
                    @reply="handleReply"
                    @save-and-close="handleSaveAndClose"
                    @consume-reply-content="activeAnnoReplyContent = ''"
            />
        </div>
    </div>
</template>

<script>
import topPanel from "./components/topPanel";
import leftPanel from "./components/leftPanel";
import centerPanel from "./components/centerPanel";
import contrastCenterPanel from "./components/contrastCenterPanel";
import rightPanel from "./components/rightPanel";
import { fetchDesignResultAuditMarkupFiles, queryFileById, addDesignResultAuditMarkupComment, fetchDesignResultAuditMarkupComments, replyDesignResultAuditMarkupComment, fetchDesignResultAuditMarkupFilesByVersion } from "@/api/audit";

export default {
    name: "annotation",
    components: { topPanel, leftPanel, centerPanel, contrastCenterPanel, rightPanel },
    data() {
        return {
            drawingGroups: [],
            activeDrawing: null,
            activeGroup: null,
            currentVersion: { version: "", time: "" },
            historyVersions: [],
            activeVersion: null,
            previousFiles: [],
            showPrevious: false,
            latestFiles: [],
            bottomBaseImageUrl: "",
            bottomCurrentPage: 1,
            bottomTotalPages: 0,
            bottomPageId: "",
            activePrevDrawing: null,
            topVersionLabel: "V2",
            prevVersionLabel: "V1",
            selectedVersionLabel: "",
            baseImageUrl: "",
            currentPage: 1,
            totalPages: 0,
            zoom: 100,
            annotationList: [],
            annotationCache: {},
            activeAnnotation: null,
            projectName: "",
            status: "",
            drawingVersion: "",
            loginName: "",
            userName: "",
            userId:"",
            currentPageId: "",
            viewMode: false,
            auditId: "",
            versionNos: [],
            previousVersionNo: null,
            taskDefinitionKey: "",
            pendingReplies: [],
            activeAnnoReplyContent: '',
            currentIsPrevDrawing: false,
        };
    },
    mounted() {
        const { auditId, projectName, loginName, userName, userId, viewMode, taskDefinitionKey } = this.$route.query;
        this.projectName = projectName || "";
        this.loginName = loginName || "";
        this.userName = userName || "";
        this.userId = userId || '';
        this.viewMode = viewMode === 'view';
        this.taskDefinitionKey = taskDefinitionKey || '';

        this.auditId = auditId;
        if (auditId) {
            this.fetchFiles(auditId);
        }
    },
    methods: {
        fetchFiles(auditId) {
            fetchDesignResultAuditMarkupFiles(auditId).then((res) => {
                if (res) {
                    this.latestFiles =  res.latestFiles || [];
                    this.previousFiles = res.previousFiles || [];
                    this.showPrevious = false;
                    const nos = new Set();
                    (res.latestFiles || []).forEach((f) => (f.versionNos || []).forEach((v) => nos.add(v)));
                    (res.previousFiles || []).forEach((f) => (f.versionNos || []).forEach((v) => nos.add(v)));
                    this.versionNos = res.versionNos;
                    this.drawingVersion = "V" + (res.latestVersionNo || "");
                    this.currentVersion = { version: this.drawingVersion, time: res.latestFiles?.[0]?.createAt || "" };
                    this.topVersionLabel = "V" + (res.latestVersionNo || "") + ".0";
                    this.prevVersionLabel = "V" + (res.previousVersionNo || "") + ".0";
                    this.previousVersionNo = res.previousVersionNo;
                    const allVerSorted = [...this.versionNos].sort((a, b) => b - a);
                    this.historyVersions = allVerSorted.slice(1).map((v) => ({
                        id: 'v-' + v,
                        version: 'V' + v,
                        versionNo: v,
                        time: (v === res.previousVersionNo && this.previousFiles[0]?.createAt) || '',
                    }));
                    this.drawingGroups = this.buildDrawingGroups(this.latestFiles);
                    if (this.drawingGroups.length > 0) {
                        // 仅选中第一个含 pages 的文件下的第一个文件，文件夹不默认选中
                        const firstGroupWithDrawings = this.drawingGroups.find(g => g.drawings && g.drawings.length > 0);
                        if (firstGroupWithDrawings) {
                            const first = firstGroupWithDrawings.drawings[0];
                            this.activeDrawing = first.id;
                            this.currentPageId = first.id || "";
                            this.loadImage(first);
                            this.currentPage = first.pageNumber;
                            this.totalPages = first.pageCount;
                            this.fetchComments(this.currentPageId);
                        }
                    }
                }
            }).catch(() => {});
        },
        loadImage(drawing, target) {
            const setUrl = (url) => {
                if (!target || target === 'top') {
                    this.baseImageUrl = '';
                    this.$nextTick(() => {
                        this.baseImageUrl = url;
                    });
                } else {
                    this.bottomBaseImageUrl = '';
                    this.$nextTick(() => {
                        this.bottomBaseImageUrl = url;
                    });
                }
            };
            if (drawing.attachmentId) {
                queryFileById(drawing.attachmentId).then((res) => {
                    const url = res && (res.completePath || res.url);
                    setUrl(url || drawing.url);
                }).catch(() => {
                    setUrl(drawing.url);
                });
            } else {
                setUrl(drawing.url);
            }
        },
        onSelectDrawing(item) {
            const oldPageId = this.currentPageId;
            this.currentIsPrevDrawing = false;
            const doDrawingSwitch = () => {
                if (this.showPrevious) {
                    this.activeDrawing = item.id;
                    this.currentPageId = item.id || "";
                    this.annotationList = [];
                    this.activeAnnotation = null;
                    this.loadImage(item, 'top');
                    this.currentPage = item.pageNumber || 1;
                    this.totalPages = item.pageCount || this.totalPages;
                    this.fetchComments(this.currentPageId, 'top');
                } else {
                    this.activeDrawing = item.id;
                    this.currentPageId = item.id || "";
                    this.annotationList = [];
                    this.activeAnnotation = null;
                    this.$refs.centerPanel && this.$refs.centerPanel.clearAll();
                    this.loadImage(item);
                    this.currentPage = item.pageNumber || 1;
                    this.totalPages = item.pageCount || this.totalPages;
                    this.fetchComments(this.currentPageId);
                }
                if (oldPageId && oldPageId !== this.currentPageId) {
                    delete this.annotationCache[oldPageId];
                }
            };
            this.doSave().catch(() => {
                this.$message.error("保存失败");
            }).then(doDrawingSwitch);
        },

        onSelectGroup(group) {
            const fileId = group.id || '';
            const doGroupSwitch = () => {
                this.activeGroup = group.id;
                this.currentIsPrevDrawing = !!group._isPrev;
                if (group.drawings && group.drawings.length > 0) {
                    const firstDrawing = group.drawings[0];
                    const oldPageId = this.currentPageId;
                    this.activeDrawing = firstDrawing.id;
                    this.currentPageId = firstDrawing.id || "";
                    this.annotationList = [];
                    this.activeAnnotation = null;
                    this.loadImage(firstDrawing);
                    this.currentPage = firstDrawing.pageNumber || 1;
                    this.totalPages = firstDrawing.pageCount || this.totalPages;
                    this.fetchComments('', fileId);
                    if (oldPageId && oldPageId !== this.currentPageId) {
                        delete this.annotationCache[oldPageId];
                    }
                } else {
                    const oldPageId = this.currentPageId;
                    this.activeDrawing = null;
                    this.currentPageId = "";
                    this.annotationList = [];
                    this.activeAnnotation = null;
                    this.totalPages = 0;
                    this.currentPage = 1;
                    if (oldPageId) {
                        delete this.annotationCache[oldPageId];
                    }
                    this.$refs.centerPanel && this.$refs.centerPanel.clearPdf();
                    this.$refs.contrastPanel && this.$refs.contrastPanel.clearPdf('top');
                    this.$refs.centerPanel && this.$refs.centerPanel.clearAll();
                    this.$refs.contrastPanel && this.$refs.contrastPanel.clearAll();
                    this.fetchComments('', fileId);
                }
            };
            if (!group._isPrev) {
                this.doSave().catch(() => {
                    this.$message.error("保存失败");
                }).then(doGroupSwitch);
            } else {
                doGroupSwitch();
            }
        },

        onSelectPrevDrawing(item) {
            const oldBottomPageId = this.bottomPageId;
            this.activePrevDrawing = item.id;
            this.currentIsPrevDrawing = true;
            this.bottomPageId = item.id || "";
            this.loadImage(item, 'bottom');
            this.bottomCurrentPage = item.pageNumber || 1;
            this.bottomTotalPages = item.pageCount || this.bottomTotalPages;
            this.fetchComments(this.bottomPageId, 'bottom');
            if (oldBottomPageId && oldBottomPageId !== this.bottomPageId) {
                delete this.annotationCache[oldBottomPageId];
            }
        },
        onSelectVersion(v) {
            this.activeVersion = v.id;
            this.selectedVersionLabel = v.version || "";
            const versionNo = v.versionNo;
            if (!versionNo) return;
            if (versionNo === this.previousVersionNo) {
                if (!this.showPrevious) {
                    this.togglePreviousFiles();
                }
                return;
            }
            fetchDesignResultAuditMarkupFilesByVersion(this.auditId, versionNo).then((res) => {
                if (res && res.length > 0) {
                    this.previousFiles = res || [];
                    this.previousVersionNo = versionNo;
                    this.prevVersionLabel = 'V' + versionNo + '.0';
                    this.showPrevious = false;
                    this.$nextTick(() => {
                        this.togglePreviousFiles();
                    });
                }
            }).catch(() => {
                this.$message.error("获取版本数据失败");
            });
        },

        togglePreviousFiles() {
            this.showPrevious = !this.showPrevious;
            if (this.showPrevious) {
                const latestGroups = this.buildDrawingGroups(this.latestFiles, false);
                const prevGroups = this.buildDrawingGroups(this.previousFiles, true);
                this.drawingGroups = [...latestGroups, ...prevGroups];
                this.$nextTick(() => {
                    if (this.currentPageId) {
                        this.fetchComments(this.currentPageId, 'top');
                    }
                });
                if (this.previousFiles.length > 0 && this.previousFiles[0].pages.length > 0) {
                    const firstPrev = this.previousFiles[0].pages[0];
                    const prevDrawing = this.pageToDrawing(firstPrev, true);
                    this.activePrevDrawing = prevDrawing.id;
                    this.bottomPageId = prevDrawing.id || "";
                    this.loadImage(prevDrawing, 'bottom');
                    this.bottomCurrentPage = prevDrawing.pageNumber || 1;
                    this.bottomTotalPages = prevDrawing.pageCount || this.bottomTotalPages;
                    this.$nextTick(() => {
                        this.fetchComments(this.bottomPageId, 'bottom');
                    });
                }
            } else {
                this.drawingGroups = this.buildDrawingGroups(this.latestFiles, false);
                this.activeGroup = null;
                this.currentIsPrevDrawing = false;
                if (this.bottomPageId) {
                    delete this.annotationCache[this.bottomPageId];
                }
                this.activePrevDrawing = null;
                this.selectedVersionLabel = "";
                this.bottomBaseImageUrl = "";
                this.bottomPageId = "";
                this.activeVersion = null;
                this.$nextTick(() => {
                    if (this.currentPageId) {
                        this.fetchComments(this.currentPageId);
                    }
                });
            }
        },

        buildDrawingGroups(files, isPrev) {
            return (files || []).map((f, fi) => ({
                id: f.fileId || "g" + (isPrev ? "p" : "") + fi,
                name: f.fileName || "",
                _isPrev: !!isPrev,
                drawings: (f.pages || []).map((p) => ({
                    ...this.pageToDrawing(p, isPrev),
                })),
            }));
        },

        pageToDrawing(p, isPrev) {
            let att = {};
            try { att = JSON.parse(p.attachment || "{}"); } catch (e) { console.log("e::",e) }
            return {
                id: p.id,
                name: p.pageName || "",
                desc: p.drawingNo || "",
                url: att.url || "",
                attachmentId: att.id || "",
                pageNumber: p.pageNumber || 1,
                pageCount: p.pageCount || 0,
                _isPrev: !!isPrev,
            };
        },
        onSelectAnnotation(id, replyContent) {
            this.activeAnnotation = id;
            if (replyContent) {
                this.activeAnnoReplyContent = replyContent;
            }
        },
        onAddAnnotation(content) {
            const now = Date.now();
            this.annotationList.push({
                id: 'anno_' + now,
                title: '',
                content,
                createBy: this.userId,
                creator: this.userName || "当前用户",
                createName: this.userName || "当前用户",
                time: new Date().toLocaleString(),
                page: this.currentPage,
                x: 0,
                y: 0,
                resolved: false,
            });

            console.log("onAddAnnotation::::this.annotationList::",this.annotationList)
        },

        onAddAnnotationFromCanvas(anno) {
            this.annotationList.push(anno);
            this.activeAnnotation = anno.id;
        },
        onCanvasCacheSync(pageId, textAnnos, shapes, rotation) {
            this.annotationCache[pageId] = { textAnnos, shapes, rotation: rotation || 0 };
            if (pageId === this.currentPageId) {
                this.annotationList = textAnnos.map(a => ({ ...a }));
            }
        },
        noop() {},
        onUpdateAnnotation({ id, title, content }) {
            const item = this.annotationList.find(m => m.id === id);
            if (item) {
                if (title !== undefined) item.title = title;
                if (content !== undefined) item.content = content;
                delete item._historical;
            }
            Object.values(this.annotationCache).forEach(cache => {
                const anno = cache.textAnnos.find(a => a.id === id);
                if (anno) {
                    if (title !== undefined) anno.title = title;
                    if (content !== undefined) anno.content = content;
                    delete anno._historical;
                }
            });
            this.$refs.centerPanel && this.$refs.centerPanel.updateAnnotation(id, title, content);
            this.$refs.contrastPanel && this.$refs.contrastPanel.updateAnnotation(id, title, content);
        },
        findFileIdByPageId(pageId) {
            for (const group of this.drawingGroups) {
                if (group.drawings && group.drawings.find(d => d.id === pageId)) {
                    return group.id;
                }
            }
            return '';
        },
        fetchComments(pageId, targetCanvasOrGroupId) {
            let fileId = '';
            let targetCanvas = targetCanvasOrGroupId;

            if (targetCanvasOrGroupId && this.drawingGroups.some(g => g.id === targetCanvasOrGroupId)) {
                fileId = targetCanvasOrGroupId;
                targetCanvas = undefined;
            } else {
                for (const group of this.drawingGroups) {
                    const drawing = group.drawings.find(d => d.id === pageId);
                    if (drawing) {
                        fileId = group.id;
                        break;
                    }
                }
            }

            fetchDesignResultAuditMarkupComments({ fileId, pageId: pageId || '' }).then((res) => {
                if (!res || !res.comments) return;
                const textAnnos = [];
                const shapes = [];
                let rotation = 0;

                res.comments.forEach(item => {
                    try {
                        const pos = JSON.parse(item.position || '{}');
                        if (item.angle !== undefined && item.angle !== null) {
                            rotation = parseInt(item.angle) || 0;
                        }
                        console.log("index:::item:::",item)
                        if (pos.type === 'comment') {
                            textAnnos.push({
                                id: item.id,
                                title: item.title === '-' ? '' : (item.title || ''),
                                content: item.content === '-' ? '' : (item.content || ''),
                                createBy: item.createBy,
                                creator: item.createName || '',
                                createName: item.createName || '',
                                createAt: item.createAt || '',
                                x: pos.x || 0,
                                y: pos.y || 0,
                                popupOffsetX: pos.popupOffsetX || '30px',
                                popupOffsetY: pos.popupOffsetY || '-10px',
                                status: item.status || '',
                                replyContent: item.replyContent || '',
                                replyName: item.replyName || '',
                                replyBy: item.replyBy || '',
                                replyAt: item.replyAt || '',
                            });
                        } else if (pos.type === 'shape' && pos.data) {
                            // 回显时保留服务端评论 id，切图/保存走 addDesignResultAuditMarkupComment 时随 shape 提交，防止重复提交
                            shapes.push({ ...pos.data, _historical: true, ...(item.id ? { id: item.id } : {}) });
                        }
                    } catch (e) { console.log("e::",e) }
                });

                this.annotationCache[pageId] = { textAnnos, shapes, rotation };
                if (targetCanvas === 'bottom') {
                    this.$refs.contrastPanel && this.$refs.contrastPanel.loadHistoricalDataForCanvas('bottom', textAnnos, shapes, rotation);
                } else {
                    this.annotationList = textAnnos;
                    this.$refs.centerPanel && this.$refs.centerPanel.loadHistoricalData(textAnnos, shapes, rotation);
                    this.$refs.contrastPanel && this.$refs.contrastPanel.loadHistoricalDataForCanvas('top', textAnnos, shapes, rotation);
                }
            }).catch(() => {});
        },
        doSave() {
            const promises = [];
            const now = new Date().toISOString();

            if (!this.viewMode) {
                Object.keys(this.annotationCache).forEach(pageId => {
                    if (pageId !== this.currentPageId) return;
                    const cache = this.annotationCache[pageId];
                    if (!cache) return;
                    const fileId = this.findFileIdByPageId(pageId) || this.activeGroup || '';

                    const comments = [];
                    const angle = cache.rotation || 0;

                    cache.textAnnos.forEach(anno => {
                        const comment = {
                            content: anno.content || "-",
                            createAt: now,
                            createBy: this.userId,
                            createName: this.userName,
                            position: JSON.stringify({
                                type: "comment",
                                x: anno.x || 0,
                                y: anno.y || 0,
                                popupOffsetX: anno.popupOffsetX || '30px',
                                popupOffsetY: anno.popupOffsetY || '-10px'
                            }),
                            title: anno.title || "-",
                            angle
                        };
                        if (anno.id && !String(anno.id).startsWith('anno_')) {
                            comment.id = anno.id;
                        }
                        comments.push(comment);
                    });

                    cache.shapes.forEach(shape => {
                        const comment = {
                            content: "-",
                            createAt: now,
                            createBy: this.userId,
                            createName: this.userName,
                            position: JSON.stringify({
                                type: "shape",
                                shapeType: shape.type,
                                data: shape
                            }),
                            title: "-",
                            angle
                        };
                        if (shape.id && !String(shape.id).startsWith('anno_')) {
                            comment.id = shape.id;
                        }
                        comments.push(comment);
                    });

                    if (comments.length) {
                        promises.push(addDesignResultAuditMarkupComment({
                            fileComments: [{
                                fileId,
                                step: this.taskDefinitionKey,
                                comments: [{ pageId, comments }]
                            }]
                        }));
                    }
                });
            }

            if (this.pendingReplies.length) {
                promises.push(
                    replyDesignResultAuditMarkupComment({ replies: this.pendingReplies }).then(() => {
                        this.pendingReplies.forEach(r => this.markAnnotationEnd(r.id));
                        this.pendingReplies = [];
                    })
                );
            }

            if (!promises.length) return Promise.resolve();
            return Promise.all(promises);
        },
        handleSave() {
            return this.doSave().then(() => {
                const fileId = this.findFileIdByPageId(this.currentPageId) || this.activeGroup || '';
                this.fetchComments(this.currentPageId, fileId);
            }).catch(() => {
                this.$message.error("保存失败");
            });
        },
        handleSaveAndClose() {
            this.doSave().then(() => {
                setTimeout(() => {
                    window.close();
                }, 1000);
            });
        },
        onDeleteAnnotation(id) {
            this.annotationList = this.annotationList.filter(m => m.id !== id);
            if (this.activeAnnotation === id) this.activeAnnotation = null;
            Object.keys(this.annotationCache).forEach(pageId => {
                const cache = this.annotationCache[pageId];
                cache.textAnnos = cache.textAnnos.filter(a => a.id !== id);
            });
            this.$refs.centerPanel && this.$refs.centerPanel.removeAnnotation(id);
            this.$refs.contrastPanel && this.$refs.contrastPanel.removeAnnotation(id);
        },
        handleReply({ id, content }) {
            const existing = this.pendingReplies.find(r => r.id === id);
            if (existing) {
                existing.replyContent = content;
            } else {
                this.pendingReplies.push({ id: id, replyContent: content });
            }
            this.syncReplyToAnnotations(id, content);
            // this.$message.success('回复已暂存');
        },
        syncReplyToAnnotations(id, content) {
            Object.keys(this.annotationCache).forEach(pageId => {
                const cache = this.annotationCache[pageId];
                const anno = (cache.textAnnos || []).find(a => a.id === id);
                if (anno) anno.replyContent = content;
            });
            const anno = this.annotationList.find(a => a.id === id);
            if (anno) anno.replyContent = content;
            this.$refs.centerPanel && this.$refs.centerPanel.updateAnnotationReply(id, content);
            this.$refs.contrastPanel && this.$refs.contrastPanel.updateAnnotationReply(id, content);
        },
        markAnnotationEnd(id) {
            Object.keys(this.annotationCache).forEach(pageId => {
                const cache = this.annotationCache[pageId];
                const anno = (cache.textAnnos || []).find(a => a.id === id);
                if (anno) anno.status = 'End';
            });
            const anno = this.annotationList.find(a => a.id === id);
            if (anno) anno.status = 'End';
            this.$refs.centerPanel && this.$refs.centerPanel.updateAnnotationStatus(id, 'End');
            this.$refs.contrastPanel && this.$refs.contrastPanel.updateAnnotationStatus(id, 'End');
        },
    },
};
</script>

<style lang="less" scoped>
.annotation-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}
.annotation-layout {
  flex: 1;
  display: flex;
  overflow: hidden;
}
</style>
